// packages
import { h } from 'preact';
import * as d3 from 'd3';
import * as moment from 'moment';
import { apmonth } from 'journalize';
import _ from 'lodash';

// data
import data from '../../../data/unemployment_rate_county_over_time.json';

// components
import PureComponent from './PureComponent';

let apMonths = new Map([
  [1, 'Jan.'],
  [2, 'Feb.'],
  [3, 'March'],
  [4, 'April'],
  [5, 'May'],
  [6, 'June'],
  [7, 'July'],
  [8, 'Aug.'],
  [9, 'Sept.'],
  [10, 'Oct.'],
  [11, 'Nov.'],
  [12, 'Dec.'],
]);

class MiniRateChange extends PureComponent {
  constructor() {
    super();

    this.getContainerWidth = this.getContainerWidth.bind(this);
    this.createChart = this.createChart.bind(this);
    this.redrawChart = this.redrawChart.bind(this);

    this.state = {
      containerWidth: null,
      bodyWidth: null,
      breakPoint: 544,
    };
  }

  createChart(county, section) {
    let { containerWidth, bodyWidth, breakPoint } = this.state;
    let width,
      height = 50;

    let margin = {
      top: 8,
      right: 35,
      left: 30,
      bottom: 5,
    };

    if (containerWidth > breakPoint) {
      width = 250;
    } else {
      width = 145;
    }

    if (county == 'Yoakum') {
      height = 65;
      margin.bottom = 17;
    }

    let chartWidth = width - margin.left - margin.right,
      chartHeight = height - margin.top - margin.bottom;

    let countyData = data.find(d => d.county == county).dates;

    // changing to moment date+time objects
    countyData.forEach(d => (d.date = moment(d.date, 'YYYY-MM-DD')));

    // grabbing totals and ranges
    let dateRange = d3.extent(countyData, d => d.date),
      totalsMax = 30; // hard coded

    let peakRate = d3.max(countyData, d => d.rate),
      peakDate = countyData
        .find(d => d.rate == peakRate)
        .date.format('YYYY-MM-DD');

    // append svg
    let svg = d3
        .select(`.mini-rate-change-${section}-${county.replace(' ', '-')}`)
        .append('svg')
        .attr('width', width)
        .attr('height', height),
      g = svg
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // set scale domains
    let x = d3
      .scaleTime()
      .domain(dateRange)
      .range([0, chartWidth]);
    let y = d3
      .scaleLinear()
      .domain([0, totalsMax])
      .range([chartHeight, 0])
      .nice();

    let xAxis = g
        .append('g')
        .attr('class', 'axis axis--x mini-rate-change-axis'),
      yAxis = g.append('g').attr('class', 'axis axis--y mini-rate-change-axis');

    if (county == 'Yoakum') {
      xAxis
        .call(
          d3
            .axisBottom(x)
            .ticks(5)
            .tickPadding(0)
            .tickValues([dateRange[0], dateRange[1]])
            .tickFormat(d => {
              return `${apMonths.get(parseInt(d.format('M')))} ${d.format(
                'YYYY'
              )}`;
            })
        )
        .attr('transform', `translate(0, ${chartHeight})`);
    }

    // yAxis.call(
    //   d3
    //     .axisLeft(y)
    //     .ticks(5)
    //     .tickPadding(10)
    //     .tickFormat(d => `${d}%`)
    // );

    let path = g
      .append('path')
      .data([countyData])
      .attr('class', 'line')
      .attr('fill', 'none')
      .attr('stroke', '#529aad')
      .attr('stroke-width', 2);

    g.append('path')
      .data([countyData])
      .attr('fill', '#529aad')
      .attr('opacity', 0.2)
      .attr(
        'd',
        d3
          .area()
          .x(function(d) {
            return x(d.date);
          })
          .y0(y(0))
          .y1(function(d) {
            return y(d.rate);
          })
      );

    // Add rate lines
    let line = d3
      .line()
      .x(d => x(d.date))
      .y(d => y(d.rate));

    path.attr('d', line);

    // label the end point
    let labels = g.append('g').attr('class', 'labeled-elements');

    let labelElements = labels
      .selectAll('g')
      .data(getLabeledData(countyData, [dateRange[0], dateRange[1], peakDate]))
      .enter()
      .append('g')
      .attr('class', 'label-element')
      .attr('transform', d => `translate(${x(d.date)}, ${y(d.rate)})`);

    labelElements
      .append('circle')
      .attr('fill', '#529aad')
      .attr('r', 3)
      .attr('class', 'label-point');

    let labelText = labelElements.append('g').attr('class', 'label-text');
    let max = d3.max(
      getLabeledData(countyData, [dateRange[0], dateRange[1], peakDate]),
      d => d.rate
    );

    labelText
      .append('text')
      .text(d => {
        if (d.rate == max && d.date.isSame(peakDate)) return `Peak: ${d.rate}%`;
        // , ${apMonths.get(parseInt(d.date.format('M')))} ${d.date.format('YYYY')}
        else return `${d.rate}%`;
      })
      .attr('font-size', '11px')
      .attr('transform', d => {
        // positioning the peak
        if (d.rate == max && d.date.isSame(peakDate))
          return 'translate(0, -10)';

        if (d.date.isSame(dateRange[0].format('YYYY-MM-DD')))
          return 'translate(-5, 0)';
        if (d.date.isSame(dateRange[1].format('YYYY-MM-DD')))
          return 'translate(5, 0)';
      })
      .attr('text-anchor', d => {
        // positioning the peak
        if (d.rate == max && d.date.isSame(peakDate)) {
          if (d.date.diff(dateRange[0]) < dateRange[1].diff(d.date))
            return 'start';
          if (d.date.diff(dateRange[0]) > dateRange[1].diff(d.date))
            return 'middle';
          else return 'middle';
        }
        if (d.date.isSame(dateRange[0].format('YYYY-MM-DD'))) return 'end';
        if (d.date.isSame(dateRange[1].format('YYYY-MM-DD'))) return 'start';
      });
    // .attr('font-weight', d => {
    //   if (d.rate == max) return 700;
    //   else return 300;
    // });

    function getLabeledData(data, dates) {
      // always annotate the latest datapoint
      let labeledData = [];

      dates.forEach(date => {
        let foundData = data.find(d => d.date.isSame(date));

        if (foundData) {
          let rateArray = labeledData.map(d => d.rate),
            dateArray = labeledData.map(d => d.date);

          if (
            rateArray.indexOf(foundData.rate) == -1 ||
            dateArray.indexOf(foundData.date) == -1
          ) {
            // don't add datapoint if it already exists
            labeledData.push(foundData);
          }
        }
      });

      return labeledData;
    }
  }

  redrawChart() {
    const { county, section } = this.props;

    d3.selectAll(
      `.mini-rate-change-${section}-${county.replace(' ', '-')} svg`
    ).remove();

    let { containerWidth, bodyWidth } = this.getContainerWidth();
    this.setState({ containerWidth: containerWidth, bodyWidth: bodyWidth });

    this.createChart(county, section);
  }

  getContainerWidth() {
    const { county, section } = this.props;

    let base = d3.select('#rate-all-table');
    const body = d3.select('body');

    const containerWidth = base.node().offsetWidth;
    const bodyWidth = body.node().offsetWidth;

    return { containerWidth, bodyWidth };
  }

  componentDidMount() {
    const { county, section } = this.props;
    let { containerWidth, bodyWidth } = this.getContainerWidth();

    this.setState(
      { containerWidth: containerWidth, bodyWidth: bodyWidth },
      () => {
        this.createChart(county, section);
      }
    );

    window.addEventListener(
      'resize',
      _.debounce(() => {
        this.redrawChart();
      }, 200)
    );
  }

  componentWillUnmount() {
    window.addEventListener(
      'resize',
      _.debounce(() => {
        this.redrawChart();
      }, 200)
    );
  }

  render() {
    const { county, section } = this.props;

    return (
      <div
        class={`mini-rate-change mini-rate-change-${section}-${county.replace(
          ' ',
          '-'
        )}`}
      ></div>
    );
  }
}

export default MiniRateChange;
